import { useEffect, useRef } from "react";
import { CommonPage, Content, PageTitle } from "common/Grid";
import { toast } from "react-toastify";
import { SalesLeadForm } from "../Common/MainForm";
import { useCreateSalesLeadMutation } from "store/action/salesLeadsApi";
import { useNavigate } from "react-router-dom";
import { captureComponentError } from "utils/sentry";

const cleanText = (value) => String(value ?? "").trim();

const CreateSalesLead = () => {
  const navigate = useNavigate();
  const [createSalesLead, { isLoading }] = useCreateSalesLeadMutation();
  const successRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!successRef.current) {
        toast.dismiss();
      }
    };
  }, []);

  const handleSubmit = async (userInput) => {
    try {
      const payload = {
        ...userInput,
        companyId: cleanText(userInput?.companyId),
        name: cleanText(userInput?.name),
        email: cleanText(userInput?.email),
        phone: cleanText(userInput?.phone),
        address: cleanText(userInput?.address),
        industry: cleanText(userInput?.industry),
        currentPlatform: cleanText(userInput?.currentPlatform),
        website: cleanText(userInput?.website),
        contactId: cleanText(userInput?.contactId),
        leadSource: cleanText(userInput?.leadSource),
        pipelineStage: cleanText(userInput?.pipelineStage),
        relationshipStatus: cleanText(userInput?.relationshipStatus),
        leadTemperature: userInput?.leadTemperature ?? 0,
        dateContacted: userInput?.dateContacted ?? "",
        lastContactDate: userInput?.lastContactDate ?? "",
        nextFollowUpDate: userInput?.nextFollowUpDate ?? "",
        timesContacted: cleanText(userInput?.timesContacted),
        estimatedMonthlyValue: cleanText(userInput?.estimatedMonthlyValue),
        estimatedCustomersPerDay: cleanText(userInput?.estimatedCustomersPerDay),
        avgSpend: cleanText(userInput?.avgSpend),
        estimatedConversionRate: cleanText(userInput?.estimatedConversionRate),
        posPaymentOpportunity: cleanText(userInput?.posPaymentOpportunity),
        strategicPartnershipPotential: cleanText(userInput?.strategicPartnershipPotential),
        sourceWorkbook: cleanText(userInput?.sourceWorkbook),
        sourceTab: cleanText(userInput?.sourceTab),
        sourceRow: cleanText(userInput?.sourceRow),
        salesRepresentative: cleanText(userInput?.salesRepresentative),
        nextStep: cleanText(userInput.nextStep),
        phonePrefix: cleanText(userInput.phonePrefix),
        estimatedAnnualAppSales: cleanText(userInput.estimatedAnnualAppSales),
        proposedFee: cleanText(userInput.proposedFee),
        estimatedAnnualAppFee: cleanText(userInput.estimatedAnnualAppFee),
        expectedCloseDate: userInput.expectedCloseDate ?? '',
        assignedTo: userInput.assignedTo || "",
        demoRejectionReason: cleanText(userInput.demoRejectionReason),
        liveRejectionReason: cleanText(userInput.liveRejectionReason),
      };
      // Only send phonePrefix when the user actually picked a country code.
      if (!payload.phonePrefix) {
        delete payload.phonePrefix;
      }
      const response = await createSalesLead({ body: payload }).unwrap();
      successRef.current = true;
      toast.success(response?.message || "Sales lead created successfully");
      navigate("/sales-leads");
    } catch (error) {
      captureComponentError(error, { component: "CreateSalesLead", action: "handleSubmit" });
      toast.error(error?.data?.message || "Something went wrong!");
    }
  };

  return (
    <CommonPage>
      <Content>
        <PageTitle title="Create Sales Lead" />
        <SalesLeadForm onSubmit={handleSubmit} isLoading={isLoading} />
      </Content>
    </CommonPage>
  );
};

export default CreateSalesLead;
